import _ from 'lodash';
import React from 'react';
import jwtDecode from 'jwt-decode';
import Navbar from '../navbar/navbar';
import FooterBar from '../footer-bar/footer-bar';
import ErrorNotification from '../error-notification/error-notification';
import Welcome from '../welcome/welcome';
import LogIn from '../log-in/log-in';
import ContactUs from '../contact-us/contact-us';
import Privacy from '../privacy/privacy';
import ExistingAppointment from '../existing-appointment/existing-appointment';
import TermsOfServiceContainer from '../terms-of-service/terms-of-service';
import Account from '../account/account';
import Condition from '../condition/condition';
import Contact from '../contact/contact';
import Location from '../location/location';
import Schedule from '../schedule/schedule';
import Review from '../review/review';
import Confirmation from '../confirmation/confirmation';
import { postAuthCode, getRequest } from '../../services/request-processor';
import {
	ACCOUNT_ROUTE, CONDITION_ROUTE, CONFIRMATION_ROUTE,
	CONTACT_COMPANY_ROUTE, CONTACT_ROUTE, CONTACT_US_ROUTE,
	ERROR_ROUTE, EXISTING_APPOINTMENT_ROUTE, GET_ASSISTANCE_ROUTE,
	HOME_ROUTE, LOCATION_ROUTE, LOG_IN_ROUTE, PRIVACY_ROUTE,
	REVIEW_ROUTE, SCHEDULE_ROUTE, TERMS_ROUTE
} from '../../constants/constants';

const emptyState = {
	language: 'EN',
	progressIndex: -1,
	canGoToHomePage: true,
	canLogOut: true,
	isLoading: false,
	isAuthenticated: false,
	inspectionRequest: {},
	location: {},
	locationType: '',
	lesseeIsPresent: 'true',
	onSiteContactFirstName: '',
	onSiteContactLastName: '',
	onSiteContactPhone: '',
	onSiteContactPhoneType: '',
	onSiteContactEmail: '',
	onSiteContactPrefMethod: '',
	contact: {},
	usingClick: false,
	selectedDate: null,
	selectedTime: null,
	isSchedulingAppt: false,
	scheduleSucceeded: false,
	currentRoute: HOME_ROUTE
};

export default class App extends React.Component {
	constructor (props) {
		super(props);
		// NOTE: I tried to get the React context working so we don't have to do the whole
		// "pass functions down as props" thing, but for the life of me I couldn't get React
		// to re-render components when the context was changed. If you can figure it out it
		// would make this app a lot simpler -- jstouffer Feb, 2023
		this.state = _.clone(emptyState);
		this.state.isIOS = (/iPad|iPhone|iPod/).test(navigator.userAgent) && !window.MSStream;

		this.clearParentState = this.clearParentState.bind(this);
		this.setParentState = this.setParentState.bind(this);
		this.setRoute = this.setRoute.bind(this);
		this.requireLogin = this.requireLogin.bind(this);

		window.addEventListener('popstate', ({state}) => {
			this.setState({ currentRoute: state.route });
		});
	}

	componentDidMount () {
		if (window.location.pathname !== '/') {
			this.setState({ currentRoute: window.location.pathname });
		}
	}

	clearParentState () {
		this.setState(emptyState);
	}

	setParentState (data) {
		this.setState(data);
	}

	setRoute (route) {
		this.setState((previousState) => {
			window.history.pushState({ route: previousState.currentRoute }, '', route);
			return { currentRoute: route };
		});
	}

	requireLogin () {
		const token = window.sessionStorage.getItem('token');
		const tokenClaimsStr = window.sessionStorage.getItem('tokenClaims');
		const tokenClaims = tokenClaimsStr ? JSON.parse(tokenClaimsStr) : {};
		const expired = (_.get(tokenClaims, 'exp', 0) * 1000) < (new Date().getTime());

		// No idea why the timing attack detection is tripping here. We're not comparing
		// secure tokens or anything.

		/* eslint-disable security/detect-possible-timing-attacks */
		if (token === null) {
			this.setRoute(LOG_IN_ROUTE);
		} else if (expired) {
			window.sessionStorage.removeItem('token');
			window.sessionStorage.removeItem('tokenClaims');
			this.setRoute(LOG_IN_ROUTE);
		}
		/* eslint-enable security/detect-possible-timing-attacks */
	}

	render () {
		// NOTE: I chose not to use React Router. See the note above on the React context. Once I gave up on
		// the context I found the amount of functionality that React Router gives vs its restrictions isn't
		// worth the effort -- jstouffer Feb, 2023
		const content = () => {
			// Since the authorization code is passed in the URL and varies it can't be handled by the switch statement.
			// This route is specifically for authorizing sso log ins such as from the hca and fmcc redirects
			if ((/authorization\/.+/).test(this.state.currentRoute)) {
				const authCode = this.state.currentRoute.split('/')[2];
				postAuthCode(authCode, (err, data) => {
					if (err) {
						this.setRoute(ERROR_ROUTE);
						return;
					}
					const token = data.body.result.token;
					const tokenClaims = jwtDecode(token);
					window.sessionStorage.setItem('token', token);
					window.sessionStorage.setItem('tokenClaims', JSON.stringify(tokenClaims));
					getRequest(tokenClaims.requestId, token, (err, data) => {
						if (err) {
							this.setRoute(ERROR_ROUTE);
							return;
						}
						this.setState({ inspectionRequest: data.body.result});
						this.setRoute(CONDITION_ROUTE);
					});
				});
			} else {
				switch (this.state.currentRoute) {
					case HOME_ROUTE:
						return (
							<Welcome
								language={this.state.language}
								setRoute={this.setRoute}
								setParentState={this.setParentState}
							/>
						);
					case LOG_IN_ROUTE:
						return (
							<LogIn
								language={this.state.language}
								setRoute={this.setRoute}
								setParentState={this.setParentState}
							/>
						);
					case ACCOUNT_ROUTE:
						this.requireLogin();
						return (
							<Account
								inspectionRequest={this.state.inspectionRequest}
								language={this.state.language}
								setRoute={this.setRoute}
								setParentState={this.setParentState}
							/>
						);
					case CONTACT_ROUTE:
						this.requireLogin();
						return (
							<Contact
								inspectionRequest={this.state.inspectionRequest}
								language={this.state.language}
								setRoute={this.setRoute}
								setParentState={this.setParentState}
							/>
						);
					case LOCATION_ROUTE:
						this.requireLogin();
						return (
							<Location
								inspectionRequest={this.state.inspectionRequest}
								isIOS={this.state.isIOS}
								language={this.state.language}
								setRoute={this.setRoute}
								setParentState={this.setParentState}
							/>
						);
					case SCHEDULE_ROUTE:
						this.requireLogin();
						return (
							<Schedule
								inspectionRequest={this.state.inspectionRequest}
								location={this.state.location}
								language={this.state.language}
								setRoute={this.setRoute}
								setParentState={this.setParentState}
							/>
						);
					case REVIEW_ROUTE:
						this.requireLogin();
						return (
							<Review
								inspectionRequest={this.state.inspectionRequest}
								locationType={this.state.locationType}
								location={this.state.location}
								contact={this.state.contact}
								language={this.state.language}
								setRoute={this.setRoute}
								setParentState={this.setParentState}
								selectedDate={this.state.selectedDate}
								selectedTime={this.state.selectedTime}
								onSiteContactEmail={this.state.onSiteContactEmail}
								onSiteContactPhone={this.state.onSiteContactPhone}
								onSiteContactPhoneType={this.state.onSiteContactPhoneType}
								onSiteContactFirstName={this.state.onSiteContactFirstName}
								onSiteContactLastName={this.state.onSiteContactLastName}
								onSiteContactPrefMethod={this.state.onSiteContactPrefMethod}
								usingClick={this.state.usingClick}
								lesseeIsPresent={this.state.lesseeIsPresent}
							/>
						);
					case CONFIRMATION_ROUTE:
						this.requireLogin();
						return (
							<Confirmation
								inspectionRequest={this.state.inspectionRequest}
								contact={this.state.contact}
								scheduleSucceeded={this.state.scheduleSucceeded}
								usingClick={this.state.usingClick}
								language={this.state.language}
								setRoute={this.setRoute}
								setParentState={this.setParentState}
							/>
						);
					case CONDITION_ROUTE:
						this.requireLogin();
						return (
							<Condition
								inspectionRequest={this.state.inspectionRequest}
								language={this.state.language}
								setRoute={this.setRoute}
								setParentState={this.setParentState}
							/>
						);
					case CONTACT_US_ROUTE:
						return (
							<ContactUs
								language={this.state.language}
								setRoute={this.setRoute}
								setParentState={this.setParentState}
								path={CONTACT_US_ROUTE}
							/>
						);
					case GET_ASSISTANCE_ROUTE:
						return (
							<ContactUs
								language={this.state.language}
								setRoute={this.setRoute}
								setParentState={this.setParentState}
								path={GET_ASSISTANCE_ROUTE}
							/>
						);
					case CONTACT_COMPANY_ROUTE:
						return (
							<ContactUs
								language={this.state.language}
								setRoute={this.setRoute}
								setParentState={this.setParentState}
								path={CONTACT_COMPANY_ROUTE}
							/>
						);
					case EXISTING_APPOINTMENT_ROUTE:
						return (
							<ExistingAppointment
								inspectionRequest={this.state.inspectionRequest}
								language={this.state.language}
								setRoute={this.setRoute}
								setParentState={this.setParentState}
							/>
						);
					case PRIVACY_ROUTE:
						return (
							<Privacy
								language={this.state.language}
								setRoute={this.setRoute}
								setParentState={this.setParentState}
							/>
						);
					case TERMS_ROUTE:
						return (
							<TermsOfServiceContainer
								language={this.state.language}
								setRoute={this.setRoute}
								setParentState={this.setParentState}
							/>
						);
					case ERROR_ROUTE:
						return (
							<ErrorNotification
								language={this.state.language}
								setRoute={this.setRoute}
								setParentState={this.setParentState}
							/>
						);
					default:
						return (
							<Welcome
								language={this.state.language}
								setRoute={this.setRoute}
								setParentState={this.setParentState}
							/>
						);
				}
			}
		};

		return (
			<div id='lss-app'>
				<Navbar
					language={this.state.language}
					canGoToHomePage={this.props.canGoToHomePage}
					canLogOut={this.props.canLogOut}
					progressIndex={this.state.progressIndex}
					isLoading={this.state.isLoading}
					setParentState={this.setParentState}
					setRoute={this.setRoute}
					clearParentState={this.clearParentState}
				/>
				{ content() }
				<FooterBar language={this.state.language} setRoute={this.setRoute} setParentState={this.setParentState} />
			</div>
		);
	}
}
